import { useCallback, useMemo } from 'react'
import keyBy from 'lodash/keyBy'
import { DepGraph } from 'dependency-graph'
import { ITemplateRule } from '@core/entities/template/TemplateRule'
import { ITemplateForm } from '@core/entities/template/TemplateForm/ITemplateForm'

/**
 * Computes which questions should be hidden based on conditional logic rules.
 *
 * Builds a directed acyclic graph (DAG) where nodes are question IDs and edges
 * mean "target question's visibility depends on condition question's answer".
 * Traverses from root nodes (no dependencies) outward, evaluating each rule to
 * determine which questions are visible.
 *
 * Supports: multi-condition rules, cross-stage backend-evaluated conditions,
 * PROJECT_IS_IN conditions (project-based visibility), and conditional question groups.
 *
 * @param currentProjectId - The project the permit is being raised in.
 *   Used to evaluate PROJECT_IS_IN conditions. Optional because the template
 *   builder doesn't have a project context.
 */
const useQuestionVisibility = ({
  rules = [],
  questions = [],
  currentProjectId,
}: ITemplateForm & { currentProjectId?: number }) => {
  // Flat lookup of all individual questions by ID (across all question groups)
  const questionsById = questions?.reduce((acc, questionGroup) => {
    const questionGroupQuestionsByKey = keyBy(questionGroup.data, 'id')
    return { ...acc, ...questionGroupQuestionsByKey }
  }, {})

  // Question groups whose conditional answer doesn't match — their children are hidden
  const hiddenConditionalQuestionGroupIdsSet = new Set(
    questions
      .filter((questionGroup) => {
        if (!questionGroup.conditionalQuestionGroup) return false
        const { submissionText, showIfAnswerIs } =
          questionGroup.conditionalQuestionGroup
        return showIfAnswerIs !== submissionText
      })
      .map((questionGroup) => questionGroup.id),
  )

  const questionGroupsById = keyBy(questions, 'id')

  // Maps questionId -> rules where that question is a condition trigger.
  // Indexes by ALL conditions so multi-condition rules appear under every controlling question.
  const rulesByDependentQuestionId = useMemo(() => {
    const result: Record<string, ITemplateRule[]> = {}
    rules?.forEach((rule) => {
      rule.conditions?.forEach((condition) => {
        if (condition.questionId) {
          if (!result[condition.questionId]) {
            result[condition.questionId] = []
          }
          result[condition.questionId].push(rule)
        }
      })
    })
    return result
  }, [rules])

  // Maps questionId -> rules that control that question's visibility.
  // Indexes by ALL shownQuestions so multi-target rules appear under every target question.
  const rulesByTargetQuestionId = useMemo(() => {
    const result: Record<string, ITemplateRule[]> = {}
    rules?.forEach((rule) => {
      rule.shownQuestions?.forEach((sq) => {
        if (sq.questionId) {
          const key = String(sq.questionId)
          if (!result[key]) result[key] = []
          result[key].push(rule)
        }
      })
    })
    return result
  }, [rules])

  // IDs of question groups that have conditional visibility (show/hide based on a prior answer)
  const conditionalQuestionGroupIds = useMemo(() => {
    return questions.reduce((acc, questionGroup) => {
      if (questionGroup.conditionalQuestionGroup) {
        return [...acc, questionGroup.id]
      }
      return acc
    }, [] as Array<string>)
  }, [])

  // All question IDs that appear as targets in any rule (candidates for being hidden)
  const ruleDependantQuestionsIds = useMemo(() => {
    return rules?.flatMap((rule) =>
      rule.shownQuestions.map((question) => question.questionId),
    )
  }, [rules])

  // DAG where nodes = question IDs, edges = "visibility depends on".
  // Node data stores the rules that the question controls.
  const dependencyGraph = useMemo(() => {
    const graph = new DepGraph()
    rules?.forEach((rule) => {
      // Register every condition's question as a graph node
      rule.conditions?.forEach((condition) => {
        const { questionId } = condition

        // PROJECT_IS_IN conditions have no questionId
        if (!questionId) {
          return
        }

        if (!graph.hasNode(questionId)) {
          graph.addNode(
            questionId,
            rulesByDependentQuestionId[questionId] || [],
          )
        }
      })

      // For each target question, add a dependency edge to every condition question
      rule.shownQuestions.forEach((shownQuestion) => {
        const shownQuestionId = String(shownQuestion.questionId)
        if (!graph.hasNode(shownQuestionId)) {
          graph.addNode(
            shownQuestionId,
            rulesByDependentQuestionId[shownQuestionId] || [],
          )
        }
        rule.conditions?.forEach((condition) => {
          if (!condition.questionId) return
          // Skip self-cycles: a question group visibility rule can produce
          // the same ID in both condition and shownQuestions.
          if (shownQuestionId === String(condition.questionId)) {
            return
          }

          graph.addDependency(shownQuestionId, condition.questionId)
        })
      })
    })

    // Wire conditional question group children to depend on their parent group's visibility
    conditionalQuestionGroupIds.forEach((questionGroupId) => {
      const questionGroup = questionGroupsById[questionGroupId]
      questionGroup?.data.forEach((questionGroupQuestion) => {
        // Skip if a child question shares the same ID as its parent group.
        if (questionGroupQuestion.id === questionGroupId) {
          return
        }

        if (!graph.hasNode(questionGroupId)) graph.addNode(questionGroupId, [])
        if (!graph.hasNode(questionGroupQuestion.id)) {
          graph.addNode(questionGroupQuestion.id, [])
        }
        graph.addDependency(questionGroupQuestion.id, questionGroupId)
      })
    })

    return graph
  }, [rules, rulesByDependentQuestionId, conditionalQuestionGroupIds])

  // Recursively walks dependants of a question, evaluating rules to determine visibility.
  // A question is visible if at least one of its controlling rules passes.
  const calculateQuestionVisibility = useCallback(
    (questionId: string, visibleQuestionIds: Set<string>) => {
      const isConditionalGroup =
        !!questionGroupsById[questionId]?.conditionalQuestionGroup
      const questionDependents = dependencyGraph.directDependantsOf(questionId)
      const questionNodeData = dependencyGraph.getNodeData(questionId)

      const questionRules = (
        Array.isArray(questionNodeData) ? questionNodeData : []
      ) as Array<ITemplateRule>

      // Evaluate each rule: all conditions must pass (AND), within each condition
      // terms are combined via conjoiningOperator (OR/AND).
      const evaluatedQuestionRules = questionRules.map((rule) => {
        const allConditionsPass = rule.conditions.every((condition) => {
          const {
            questionId,
            conjoiningOperator,
            terms,
            conditionQuestionSet,
          } = condition

          // Backend pre-evaluated cross-stage condition — trust the result
          if (
            conditionQuestionSet?.isBackendEvaluatedTrue !== null &&
            conditionQuestionSet?.isBackendEvaluatedTrue !== undefined
          ) {
            return conditionQuestionSet.isBackendEvaluatedTrue
          }

          // PROJECT_IS_IN: check if the current project matches the condition's project list.
          // If no currentProjectId is provided (e.g. template builder), assume satisfied.
          if (condition.type === 'PROJECT_IS_IN') {
            if (!currentProjectId) {
              return true
            }

            return condition.projectIds?.includes(currentProjectId)
          }

          // Local evaluation for same-stage QUESTION conditions
          const conditionQuestion = questionsById[questionId]
          const termChecker = (term) => {
            const selectedAnswers = conditionQuestion?.answers.filter(
              ({ selected }) => selected,
            )
            return selectedAnswers?.some(
              (answer) => answer.id === term.answerId,
            )
          }

          return conjoiningOperator === 'OR'
            ? terms?.some(termChecker)
            : terms?.every(termChecker)
        })

        return { ...rule, passes: allConditionsPass }
      })

      dependencyGraph.setNodeData(questionId, evaluatedQuestionRules)

      questionDependents.forEach((questionDependent) => {
        const questionDependentRules = evaluatedQuestionRules.filter((rule) => {
          return rule.shownQuestions.some(
            ({ questionId }) => questionId === questionDependent,
          )
        })

        const childNodeDirectDependencies =
          dependencyGraph.directDependenciesOf(questionDependent)

        const hasHiddenQuestionGroupDependencies =
          childNodeDirectDependencies.some((nodeDependant) =>
            hiddenConditionalQuestionGroupIdsSet.has(nodeDependant),
          )

        const nodePassesRules =
          (isConditionalGroup && childNodeDirectDependencies.length === 1) ||
          questionDependentRules.some(({ passes }) => passes)

        if (!hasHiddenQuestionGroupDependencies && nodePassesRules) {
          visibleQuestionIds.add(questionDependent)
          calculateQuestionVisibility(questionDependent, visibleQuestionIds)
        }
      })
    },
    [dependencyGraph, questionsById],
  )

  // Start from root nodes (questions with no dependencies — always visible) and walk
  // outward through the graph. Any rule-dependent question not reached is hidden.
  const hiddenQuestionIds = useMemo(() => {
    const rootNodes = dependencyGraph.overallOrder(true)
    const visibleQuestionIds = new Set<string>(rootNodes)

    // PROJECT_IS_IN conditions have no questionId, so their targets aren't wired
    // into the graph and default to visible as root nodes. Remove them before
    // traversal when the current project doesn't match. If another passing rule
    // targets the same question, the traversal will re-add it.
    if (currentProjectId) {
      rules?.forEach((rule) => {
        const hasFailingProjectCondition = rule.conditions?.some(
          (c) =>
            c.type === 'PROJECT_IS_IN' &&
            !c.projectIds?.includes(currentProjectId),
        )
        if (hasFailingProjectCondition) {
          rule.shownQuestions?.forEach((sq) => {
            visibleQuestionIds.delete(String(sq.questionId))
          })
        }
      })
    }

    rootNodes.forEach((node) =>
      calculateQuestionVisibility(node, visibleQuestionIds),
    )

    return ruleDependantQuestionsIds
      .concat(
        conditionalQuestionGroupIds.flatMap((id) =>
          questionGroupsById[id]?.data.map((question) => question.id),
        ),
      )
      .filter((questionId) => {
        return !visibleQuestionIds.has(String(questionId))
      })
  }, [
    dependencyGraph,
    calculateQuestionVisibility,
    ruleDependantQuestionsIds,
    currentProjectId,
  ])

  return {
    hiddenQuestionIds,
    rulesByDependentQuestionId,
    rulesByTargetQuestionId,
  }
}

export { useQuestionVisibility }
